import { useMemo } from 'react';
import { useTable } from 'react-table';
import { Image, Text } from '@ui/atoms';

const FdComparisonTable = ({
    fdCompareContent,
    isMobile,
    data,
    isExpanded,
}) => {
    const mobileColumns = useMemo(
        () => [
            {
                Header: 'Bank',
                accessor: 'bank',
                Cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <Image
                            src={`/products/fixed-deposit/${row.original.logo}.svg`}
                            alt={row.original.bank}
                            className="w-6 h-6"
                        />
                        <Text
                            content={row.original.bank}
                            className="h7-medium text-primary-500"
                        />
                    </div>
                ),
                maxWidth: 190,
            },
            {
                Header: 'Interest rate for general citizen (1Y)',
                accessor: 'year1',
                maxWidth: 95,
            },
            {
                Header: 'Interest rate for general citizen (3Y)',
                accessor: 'year3',
                maxWidth: 100,
            },
        ],
        []
    );
    const desktopData = useMemo(
        () => [
            {
                duration: 'Interest rate for \n general citizen (1Y)',
                ...fdCompareContent.reduce(
                    (acc, bank) => ({ ...acc, [bank.bank]: bank.year1 }),
                    {}
                ),
            },
            {
                duration: 'Interest rate for \n general citizen (3Y)',
                ...fdCompareContent.reduce(
                    (acc, bank) => ({ ...acc, [bank.bank]: bank.year3 }),
                    {}
                ),
            },
        ],
        []
    );
    const desktopColumns = useMemo(
        () => [
            {
                Header: 'Bank',
                accessor: 'duration',
                fixed: 'left',
                maxWidth: 160,
                width: 160,
            },
            ...data.map((bank) => ({
                Header: (
                    <div className="flex items-center gap-2">
                        <Image
                            src={`/products/fixed-deposit/${bank.logo}.svg`}
                            alt={bank.bank}
                            className="w-8 h-8"
                        />
                        <Text
                            content={bank.bank}
                            className="h7-medium text-primary-500"
                            htmlTag="span"
                        />
                    </div>
                ),
                accessor: bank.bank,
                maxWidth: 182,
                width: 182,
            })),
        ],
        []
    );

    const visibleData = isMobile && !isExpanded ? data.slice(0, 4) : data;
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({
            columns: isMobile ? mobileColumns : desktopColumns,
            data: isMobile ? visibleData : desktopData,
        });

    return (
        <div
            className={`relative  w-full border rounded-xl scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200  thin-scrollbar comparison-table ${
                isMobile ? 'h-auto overflow-hidden' : 'overflow-x-auto'
            }`}
        >
            <table
                {...getTableProps()}
                className="w-full border-collapse min-w-max"
            >
                <thead>
                    {headerGroups.map((headerGroup, index) => (
                        <tr
                            {...headerGroup.getHeaderGroupProps()}
                            className="bg-gray-100"
                            key={'row' + index}
                        >
                            {headerGroup.headers.map((column, i) => (
                                <th
                                    {...column.getHeaderProps()}
                                    key={'column' + i}
                                    className={`px-2 md:px-3 py-3 text-left border-b border-r last:border-r-0 border-gray-200 ${
                                        !isMobile
                                            ? column.fixed === 'left'
                                                ? 'sticky left-0 bg-gray-50 shadow-fd-table-shadow h6-medium text-gray-500'
                                                : 'bg-basicWhite p4-medium text-gray-700 break-keep'
                                            : 'h7-medium text-gray-500'
                                    }`}
                                >
                                    {column.render('Header')}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, index) => {
                        prepareRow(row);
                        return (
                            <tr
                                {...row.getRowProps()}
                                className="border-b odd:bg-basicWhite even:bg-gray-50 last:border-b md:border-b-0 md:odd:border-b"
                                key={'row' + index}
                            >
                                {row.cells.map((cell, i) => (
                                    <td
                                        {...cell.getCellProps({
                                            style: isMobile
                                                ? {
                                                      minWidth:
                                                          cell.column.minWidth,
                                                      width: cell.column.width,
                                                      maxWidth:
                                                          cell.column.maxWidth,
                                                  }
                                                : {
                                                      width: cell.column.width,
                                                  },
                                        })}
                                        key={'column' + i}
                                        className={`px-2 md:px-3 py-3 border-r last:border-r-0 border-gray-200 text-left ${
                                            !isMobile
                                                ? cell.column.fixed === 'left'
                                                    ? 'sticky left-0 bg-gray-50 shadow-fd-table-shadow text-gray-500 h6-medium'
                                                    : 'bg-basicWhite p3-medium text-gray-700 '
                                                : 'p5-medium'
                                        }`}
                                    >
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default FdComparisonTable;
